import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  HelpCircle, 
  ChevronDown, 
  Search, 
  MessageCircle, 
  FileSpreadsheet, 
  ShieldCheck, 
  Download, 
  Sparkles,
  CheckCircle2,
  Database,
  ArrowRight
} from 'lucide-react';
import { db, collection, onSnapshot, query, addDoc, handleFirestoreError, OperationType } from '@/firebase';
import { useAuth } from '@/context/AuthContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Helmet } from 'react-helmet-async';

export interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category: string;
  order: number;
}

const DEFAULT_FAQS: Omit<FAQItem, 'id'>[] = [
  {
    order: 1,
    category: 'Format Dokumen',
    question: 'Format file apa saja yang akan saya dapatkan setelah membeli produk konstruksi?',
    answer: 'Anda akan mendapatkan file dokumen dalam format Microsoft Excel (.xlsx), Microsoft Word (.docx), AutoCAD (.dwg), serta salinan PDF siap cetak. Semua dokumen 100% editable (bebas diedit), tidak dikunci kata sandi (unlocked), dan tabel perhitungan otomatis sudah dilengkapi formula baku yang siap digunakan.'
  },
  {
    order: 2,
    category: 'Standar & Regulasi',
    question: 'Apakah dokumen SMKK dan RAB sudah memenuhi regulasi Permen PUPR & LPSE terbaru?',
    answer: 'Ya, seluruh format Sistem Manajemen Keselamatan Konstruksi (SMKK), Rencana Keselamatan Konstruksi (RKK), Rencana Mutu Pekerjaan Konstruksi (RMPK), dan Analisis Harga Satuan Pekerjaan (AHSP) mengacu ketat pada Permen PUPR No. 10/2021 serta standar evaluasi dokumen penawaran tender LPSE/PBJ konstruksi terbaru.'
  },
  {
    order: 3,
    category: 'Pembayaran & Unduh',
    question: 'Bagaimana alur pengunduhan dokumen setelah melakukan pembayaran?',
    answer: 'Setelah pembayaran Anda dikonfirmasi (melalui transfer bank atau QRIS), status pesanan akan langsung berubah menjadi selesai (completed). File dokumen digital akan seketika aktif dan dapat diunduh tanpa batas waktu melalui menu Dashboard Akun Anda di platform Azman204.'
  },
  {
    order: 4,
    category: 'Format Dokumen',
    question: 'Apakah formula dan koefisien dalam file RAB Excel bisa disesuaikan dengan harga lokal?',
    answer: 'Sangat bisa. Lembar kerja spreadsheet RAB kami terbuka penuh (unlocked). Anda bebas mengganti daftar upah minimum regional (UMR), harga bahan material setempat, biaya sewa alat berat, maupun menambahkan item pekerjaan baru sesuai kebutuhan spesifik proyek Anda.'
  },
  {
    order: 5,
    category: 'Konsultasi & Layanan',
    question: 'Apakah Azman204 menerima jasa penyusunan dokumen tender kustom atau review RAB?',
    answer: 'Tentu saja. Selain produk template siap pakai, tim estimator dan tenaga ahli konstruksi kami siap membantu penyusunan dokumen tender terarah, review kelayakan RAB, pembuatan metode pelaksanaan khusus, hingga asistensi teknis melalui tombol Chat Admin WhatsApp kami.'
  },
  {
    order: 6,
    category: 'Dukungan & Garansi',
    question: 'Bagaimana jika saya mengalami kendala teknis saat membuka atau mengekstrak file?',
    answer: 'Kami menyediakan garansi dukungan teknis penuh. Jika file korup, tidak kompatibel dengan versi software Anda, atau tautan unduh bermasalah, silakan hubungi tim support WhatsApp kami. Tim kami akan segera mengirimkan ulang berkas atau memandu Anda hingga file siap dipakai.'
  },
  {
    order: 7,
    category: 'Standar & Regulasi',
    question: 'Apakah bertransaksi di platform Azman204 aman dan terpercaya?',
    answer: 'Platform Azman204 dirancang dengan sistem autentikasi aman, pencatatan transaksi transparan di akun pengguna, dan konfirmasi pembayaran yang tervalidasi. Layanan kami telah dipercaya oleh ratusan kontraktor, konsultan perencana/pengawas, dan mandor di berbagai wilayah Indonesia.'
  }
];

export default function FAQAccordion() {
  const { isAdmin } = useAuth();
  const [faqs, setFaqs] = useState<FAQItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [openIds, setOpenIds] = useState<Record<string, boolean>>({ 'default-1': true });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('Semua');
  const [isSeeding, setIsSeeding] = useState(false);

  // Subscribe to real-time FAQs in Firestore
  useEffect(() => {
    let unsubscribe = () => {};
    try {
      const q = collection(db, 'faqs');
      unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          if (!snapshot.empty) {
            const items: FAQItem[] = snapshot.docs.map((doc) => {
              const data = doc.data();
              return {
                id: doc.id,
                question: data.question || '',
                answer: data.answer || '',
                category: data.category || 'Format Dokumen',
                order: data.order ?? 99,
              };
            });
            // Sort by order ascending
            items.sort((a, b) => (a.order || 0) - (b.order || 0));
            setFaqs(items);
            // Open the first item by default
            if (items.length > 0) {
              setOpenIds((prev) => ({ ...prev, [items[0].id]: true }));
            }
          } else {
            // Fallback to default items if Firestore collection is not yet populated
            const initialDefault = DEFAULT_FAQS.map((item, idx) => ({
              ...item,
              id: `default-${idx + 1}`
            }));
            setFaqs(initialDefault);
            setOpenIds({ 'default-1': true });
          }
          setLoading(false);
        },
        (error) => {
          console.warn("Gagal berlangganan data FAQ dari Firestore, memuat data bawaan:", error);
          handleFirestoreError(error, OperationType.LIST, 'faqs');
          const fallback = DEFAULT_FAQS.map((item, idx) => ({
            ...item,
            id: `default-${idx + 1}`
          }));
          setFaqs(fallback);
          setLoading(false);
        }
      );
    } catch (err) {
      console.error("Inisialisasi Firestore FAQ error:", err);
      const fallback = DEFAULT_FAQS.map((item, idx) => ({
        ...item,
        id: `default-${idx + 1}`
      }));
      setFaqs(fallback);
      setLoading(false);
    }

    return () => unsubscribe();
  }, []);

  const toggleAccordion = (id: string) => {
    setOpenIds((prev) => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Seed default FAQs to Firestore (Admin only)
  const handleSeedDefaults = async () => {
    setIsSeeding(true);
    try {
      for (const faq of DEFAULT_FAQS) {
        await addDoc(collection(db, 'faqs'), {
          ...faq,
          createdAt: new Date(),
          updatedAt: new Date()
        });
      }
      toast.success("7 Data Tanya-Jawab konstruksi berhasil disimpan ke Firestore!");
    } catch (err: any) {
      console.error("Gagal melakukan seeding FAQ:", err);
      toast.error("Gagal menyimpan ke Firestore: " + (err.message || 'Periksa aturan keamanan'));
    } finally {
      setIsSeeding(false);
    }
  };

  // Extract unique categories
  const categories = ['Semua', ...Array.from(new Set(faqs.map((f) => f.category)))];

  // Filter FAQs based on search and category
  const filteredFaqs = faqs.filter((faq) => {
    const matchesCategory = selectedCategory === 'Semua' || faq.category === selectedCategory;
    const queryLower = searchQuery.toLowerCase().trim();
    const matchesSearch = 
      !queryLower || 
      faq.question.toLowerCase().includes(queryLower) || 
      faq.answer.toLowerCase().includes(queryLower) ||
      faq.category.toLowerCase().includes(queryLower);

    return matchesCategory && matchesSearch;
  });

  // Construct dynamic FAQPage JSON-LD Schema for Google Rich Results
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((faq) => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <section id="faq" className="py-24 bg-background relative overflow-hidden transition-colors duration-300">
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>
      {/* Background Subtle Ambient Glows */}
      <div className="absolute top-1/4 left-0 w-80 h-80 bg-gold/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-gold/10 border border-gold/30 text-gold text-xs font-black uppercase tracking-widest mb-4">
            <HelpCircle className="h-3.5 w-3.5" />
            Tanya Jawab & Transparansi
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-foreground tracking-tight leading-tight mb-4">
            Pertanyaan Umum Seputar <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold to-gold-light">Dokumen Konstruksi</span>
          </h2>
          <p className="text-foreground/60 text-base sm:text-lg font-medium leading-relaxed">
            Semua yang perlu Anda ketahui mengenai spesifikasi format file, legalitas Permen PUPR, keamanan pembelian, hingga tata cara pengunduhan.
          </p>
        </div>

        {/* Search & Category Filter */}
        <div className="space-y-4 mb-10">
          <div className="relative max-w-xl mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-foreground/40" />
            <Input 
              type="text"
              placeholder="Cari pertanyaan (contoh: Excel, SMKK, RAB, LPSE)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 pr-4 h-12 rounded-2xl bg-card border-border/80 text-foreground placeholder:text-foreground/40 shadow-sm focus:border-gold focus:ring-gold"
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-bold text-foreground/50 hover:text-foreground"
              >
                Hapus
              </button>
            )}
          </div>

          {/* Category Chips */}
          <div className="flex flex-wrap items-center justify-center gap-2 pt-2">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all ${
                  selectedCategory === cat 
                    ? 'bg-gold text-navy shadow-md shadow-gold/20 scale-105' 
                    : 'bg-card text-foreground/70 hover:text-foreground hover:bg-muted border border-border/60'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Admin Quick Seed Banner if Firestore is not populated */}
        {isAdmin && faqs.some((f) => f.id.startsWith('default-')) && (
          <div className="mb-8 p-4 rounded-2xl bg-amber-500/10 border border-amber-500/30 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Database className="h-5 w-5 text-amber-500 shrink-0" />
              <div className="text-left text-xs sm:text-sm">
                <span className="font-bold text-foreground block">Admin Notice: Menggunakan FAQ Bawaan</span>
                <span className="text-foreground/60">Simpan 7 data tanya-jawab konstruksi ini secara permanen ke koleksi Firestore database Anda.</span>
              </div>
            </div>
            <Button
              onClick={handleSeedDefaults}
              disabled={isSeeding}
              size="sm"
              className="bg-amber-600 hover:bg-amber-500 text-white font-bold rounded-xl shrink-0"
            >
              {isSeeding ? "Menyimpan ke Firestore..." : "Simpan ke Firestore"}
            </Button>
          </div>
        )}

        {/* Accordion List */}
        {loading ? (
          <div className="py-16 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold mx-auto mb-3" />
            <p className="text-foreground/50 text-sm font-medium">Memuat tanya jawab umum...</p>
          </div>
        ) : filteredFaqs.length === 0 ? (
          <div className="text-center py-16 bg-card border border-dashed border-border rounded-3xl p-8">
            <HelpCircle className="h-12 w-12 text-foreground/30 mx-auto mb-3" />
            <h4 className="text-lg font-bold text-foreground mb-1">Pertanyaan Tidak Ditemukan</h4>
            <p className="text-foreground/60 text-sm max-w-md mx-auto mb-6">
              Tidak ada hasil yang sesuai dengan kata kunci &ldquo;{searchQuery}&rdquo;. Tim kami siap menjawab pertanyaan Anda secara langsung.
            </p>
            <Button
              asChild
              className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl"
            >
              <a 
                href={`https://wa.me/6281261089400?text=${encodeURIComponent(`Halo Admin Azman204, saya ingin bertanya mengenai: ${searchQuery}`)}`}
                target="_blank" 
                rel="noopener noreferrer"
              >
                <MessageCircle className="h-4 w-4 mr-2" />
                Tanyakan via WhatsApp
              </a>
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredFaqs.map((faq, index) => {
              const isOpen = Boolean(openIds[faq.id]);
              return (
                <motion.div
                  key={faq.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25, delay: index * 0.04 }}
                  className={`rounded-2xl border transition-all duration-300 overflow-hidden bg-card ${
                    isOpen 
                      ? 'border-gold/50 shadow-lg shadow-gold/5' 
                      : 'border-border/70 hover:border-gold/30'
                  }`}
                >
                  <button
                    onClick={() => toggleAccordion(faq.id)}
                    aria-expanded={isOpen}
                    className="w-full text-left p-5 sm:p-6 flex items-start sm:items-center justify-between gap-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold"
                  >
                    <div className="flex items-start sm:items-center gap-3.5 sm:gap-4 flex-1">
                      <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 mt-0.5 sm:mt-0 transition-colors ${
                        isOpen 
                          ? 'bg-gold text-navy shadow-sm' 
                          : 'bg-muted text-foreground/50'
                      }`}>
                        <HelpCircle className="h-4 w-4" />
                      </div>
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <Badge 
                            variant="secondary" 
                            className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-md bg-muted text-foreground/60"
                          >
                            {faq.category}
                          </Badge>
                        </div>
                        <h3 className={`text-base sm:text-lg font-bold transition-colors ${
                          isOpen ? 'text-gold' : 'text-foreground'
                        }`}>
                          {faq.question}
                        </h3>
                      </div>
                    </div>

                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-transform duration-300 ${
                      isOpen ? 'rotate-180 bg-gold/10 text-gold' : 'text-foreground/40 hover:bg-muted'
                    }`}>
                      <ChevronDown className="h-5 w-5" />
                    </div>
                  </button>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        key="content"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.28, ease: 'easeInOut' }}
                      >
                        <div className="px-5 sm:px-6 pb-6 pt-1 border-t border-border/40">
                          <div className="pl-0 sm:pl-12 text-sm sm:text-base text-foreground/80 leading-relaxed font-normal">
                            {faq.answer}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Bottom Trust & Direct Assistance Card */}
        <div className="mt-14 p-6 sm:p-8 rounded-3xl bg-gradient-to-r from-navy via-slate-900 to-navy text-white border border-gold/30 shadow-2xl flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4 text-center md:text-left flex-col md:flex-row">
            <div className="w-14 h-14 rounded-2xl bg-gold/20 border border-gold/40 flex items-center justify-center text-gold shrink-0">
              <Sparkles className="h-7 w-7" />
            </div>
            <div>
              <h4 className="text-xl sm:text-2xl font-black text-white mb-1">
                Masih Memiliki Pertanyaan Spesifik?
              </h4>
              <p className="text-white/70 text-sm max-w-xl">
                Estimator dan konsultan Azman204 siap memberikan penjelasan langsung, konsultasi format RAB, atau permohonan sampel dokumen gratis.
              </p>
            </div>
          </div>

          <div className="shrink-0 w-full sm:w-auto">
            <Button
              asChild
              className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-500 text-white font-bold h-12 px-6 rounded-xl shadow-lg shadow-emerald-600/30 flex items-center justify-center gap-2 text-sm"
            >
              <a
                href="https://wa.me/6281261089400?text=Halo%20Admin%20Azman204,%20saya%20ingin%20berkonsultasi%20mengenai%20produk%20dan%20format%20dokumen%20konstruksi."
                target="_blank"
                rel="noopener noreferrer"
              >
                <MessageCircle className="h-5 w-5 fill-current" />
                Chat WhatsApp Admin
                <ArrowRight className="h-4 w-4" />
              </a>
            </Button>
          </div>
        </div>

      </div>
    </section>
  );
}
